import { formatMoney, money, type CurrencyCode } from '@apis/domain';
import { cn } from '../lib/utils';

/**
 * Money is integer minor units everywhere; it is formatted only at the edge, always with
 * the Indonesian convention (`Rp 180.000`, DESIGN.md §8), whatever the UI language.
 */
export const MONEY_LOCALE = 'id-ID';

/** Pure formatting (exported for tests and non-React callers). */
export function formatMoneyDisplay(amountMinor: number, currency: CurrencyCode): string {
  return formatMoney(money(amountMinor, currency), MONEY_LOCALE).replace(/\u00a0/g, ' ');
}

/**
 * Compact money for chart axes (`Rp 1,2 jt`). Uses whole major units; precision loss is
 * acceptable for axis labels only — never for amounts a user acts on.
 */
export function formatMoneyCompact(amountMinor: number, currency: CurrencyCode): string {
  const exp = currency === 'IDR' || currency === 'JPY' || currency === 'KRW' ? 0 : 2;
  const major = Math.round(amountMinor / 10 ** exp);
  return new Intl.NumberFormat(MONEY_LOCALE, {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: 1,
  })
    .format(major)
    .replace(/\u00a0/g, ' ');
}

/**
 * Renders an amount with tabular numerals so columns of money align. Negative amounts
 * keep their minus sign in the text (colour is never the only signal).
 */
export function MoneyDisplay({
  amountMinor,
  currency,
  className,
}: {
  amountMinor: number;
  currency: CurrencyCode;
  className?: string;
}) {
  return (
    <span
      data-negative={amountMinor < 0 || undefined}
      className={cn('whitespace-nowrap tabular-nums', className)}
    >
      {formatMoneyDisplay(amountMinor, currency)}
    </span>
  );
}
